// src/pages/MPFlow/phases/Phase5Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import EdyMascot from '../components/EdyMascot';
import apiClient from '../../../lib/apiClient';

// SVG Bar Chart puro
function BarChart({ dates, impressions, clicks }) {
  const W = 540, H = 160, PL = 36, PR = 12, PT = 12, PB = 28;
  const cW = W - PL - PR;
  const cH = H - PT - PB;
  const n  = dates.length;
  if (n === 0) return <p className="mp-chart-empty">Sem dados ainda</p>;

  const maxImp = Math.max(...impressions, 1);
  const barW   = Math.max(4, (cW / n) * 0.55);
  const gap    = cW / n;

  const bx = (i) => PL + i * gap + gap / 2 - barW / 2;
  const by = (v) => PT + cH - (v / maxImp) * cH;
  const bh = (v) => (v / maxImp) * cH;

  const yTick = Math.round(maxImp / 2);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="mp-bar-chart" preserveAspectRatio="xMidYMid meet">
      {[0, yTick, maxImp].map(t => (
        <line key={t}
          x1={PL} y1={by(t).toFixed(1)}
          x2={W - PR} y2={by(t).toFixed(1)}
          stroke="rgba(255,255,255,0.07)" strokeDasharray="4 3"
        />
      ))}
      {impressions.map((v, i) => (
        <rect key={`imp-${i}`}
          x={bx(i)} y={by(v).toFixed(1)}
          width={barW} height={bh(v).toFixed(1)}
          rx="3" fill="rgba(6,182,212,0.65)"
        />
      ))}
      {clicks.map((v, i) => (
        <rect key={`clk-${i}`}
          x={bx(i) + barW * 0.6} y={by(v).toFixed(1)}
          width={barW * 0.6} height={bh(v).toFixed(1)}
          rx="2" fill="rgba(126,34,206,0.8)"
        />
      ))}
      {dates.map((d, i) => (
        <text key={i} x={bx(i) + barW / 2} y={H - 6}
          textAnchor="middle" className="mp-bar-chart__tick">
          {d.slice(5)}
        </text>
      ))}
      <text x={PL - 4} y={by(maxImp) + 4} textAnchor="end" className="mp-bar-chart__tick">{maxImp}</text>
      <text x={PL - 4} y={by(0) + 4}      textAnchor="end" className="mp-bar-chart__tick">0</text>
    </svg>
  );
}

export default function Phase5Dashboard({ campaignId, onBack }) {
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (!campaignId) { setLoading(false); return; }
    apiClient.request(`/mp/dashboard/${campaignId}`, { method: 'GET' })
      .then(res => {
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        setError('Erro ao carregar dados de performance.');
        setLoading(false);
      });
  }, [campaignId]);

  if (loading) {
    return (
      <div className="mp-loading">
        <div className="mp-loading__icon"><ChartBarIcon className="icon-xl" /></div>
        <div className="mp-loading__dots"><span /><span /><span /></div>
        <p className="mp-loading__msg">Carregando sua performance...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mp-card">
        <div className="mp-alert mp-alert--error">{error}</div>
        <button className="mp-btn mp-btn--ghost" onClick={() => navigate('/mp')}>
          ← Voltar ao início
        </button>
      </div>
    );
  }

  const perf        = data?.performance || {};
  const dates       = perf.dates        || [];
  const impressions = perf.impressions  || [];
  const clicks      = perf.clicks       || [];
  const totalImp    = data?.total_impressions ?? impressions.reduce((a, b) => a + b, 0);
  const totalClk    = data?.total_clicks      ?? clicks.reduce((a, b) => a + b, 0);
  const ctr         = data?.ctr ?? 0;

  return (
    <div>
      <EdyMascot phase="vendas" />

      <div className="mp-card mp-dash-header">
        <h2 className="mp-phase-title">Performance da Campanha</h2>
        <p className="mp-phase-sub">
          Status: <strong className={data?.status === 'active' ? 'mp-text-active' : 'mp-text-warning'}>
            {data?.status === 'active' ? '● Ativa' : data?.status || 'draft'}
          </strong>
        </p>
      </div>

      <div className="mp-dash-stats">
        <div className="mp-stat-card">
          <div className="mp-stat-card__val">{totalImp}</div>
          <div className="mp-stat-card__label">Impressões</div>
        </div>
        <div className="mp-stat-card">
          <div className="mp-stat-card__val">{totalClk}</div>
          <div className="mp-stat-card__label">Cliques</div>
        </div>
        <div className="mp-stat-card">
          <div className="mp-stat-card__val">{ctr}%</div>
          <div className="mp-stat-card__label">CTR</div>
        </div>
      </div>

      {dates.length > 0 && (
        <div className="mp-chart-wrap">
          <p className="mp-chart-title">
            Impressões
            <span className="mp-chart-legend">
              <span className="mp-chart-legend__dot mp-chart-legend__dot--imp">■</span> Impressões &nbsp;
              <span className="mp-chart-legend__dot mp-chart-legend__dot--clk">■</span> Cliques
            </span>
          </p>
          <BarChart dates={dates} impressions={impressions} clicks={clicks} />
        </div>
      )}

      {totalImp >= 1000 && (
        <div className="mp-alert mp-alert--success">
          Sua campanha ultrapassou 1.000 impressões! Parabéns!
        </div>
      )}

      <div className="mp-btn-row">
        <button
          className="mp-btn mp-btn--ghost mp-btn--sm"
          onClick={() => navigate(`/mp/dashboard/${campaignId}`)}
        >
          Abrir Dashboard Completo
        </button>
        <button className="mp-btn mp-btn--cyan mp-btn--sm" onClick={() => navigate('/mp')}>
          Nova Campanha
        </button>
      </div>
    </div>
  );
}
